import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Search, Store, User, CreditCard, ClipboardList, FileText, FilePlus, ShoppingBag, CheckCircle, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentsAPI, salesOrdersAPI, salesInvoicesAPI, purchasesAPI, creditNotesAPI, promotionsAPI } from '../../services/api';
import { ModernCard, ModernBadge } from '../ui/ModernComponents';

const Header = ({ onMenuToggle, isMobile }) => {
  const { user, getUserStore, isEmployee } = useAuth();
  const userStore = getUserStore();

  const [notifications, setNotifications] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    // Fetch payment notifications (overdue or incomplete)
    let paymentNotifs = [];
    try {
      const res = await paymentsAPI.exportPayments({});
      const text = await res.data.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',');
      const data = lines.slice(1).filter(Boolean).map(line => {
        const values = line.split(',');
        const obj = {};
        headers.forEach((h, i) => { obj[h] = values[i]; });
        return obj;
      });
      paymentNotifs = data.filter(p => (Number(p.amount) === 0 || Number(p.amount) < 100) && new Date(p.payment_date) < new Date())
        .map(p => ({
          type: 'Paiement',
          id: p.id,
          reference_type: p.reference_type,
          reference_id: p.reference_id,
          amount: p.amount,
          date: p.payment_date,
          status: 'En retard',
        }));
    } catch {}
    // Fetch new commandes (orders)
    let orderNotifs = [];
    try {
      const res = await salesOrdersAPI.getSalesOrders();
      orderNotifs = res.data.filter(o => o.status === 'nouveau' || o.status === 'new').map(o => ({
        type: 'Commande',
        id: o.id,
        reference_type: 'Commande',
        reference_id: o.id,
        date: o.created_at || o.date,
        status: 'Nouvelle commande',
      }));
    } catch {}
    // Fetch new factures (invoices)
    let invoiceNotifs = [];
    try {
      const res = await salesInvoicesAPI.getSalesInvoices();
      invoiceNotifs = res.data.filter(f => f.status === 'nouveau' || f.status === 'new').map(f => ({
        type: 'Facture',
        id: f.id,
        reference_type: 'Facture',
        reference_id: f.id,
        date: f.created_at || f.date,
        status: 'Nouvelle facture',
      }));
    } catch {}
    // Fetch new avoirs (credit notes)
    let creditNoteNotifs = [];
    try {
      const res = await creditNotesAPI.getCreditNotes();
      creditNoteNotifs = res.data.filter(a => a.status === 'nouveau' || a.status === 'new').map(a => ({
        type: 'Avoir',
        id: a.id,
        reference_type: 'Avoir',
        reference_id: a.id,
        date: a.date,
        status: 'Nouvel avoir',
      }));
    } catch {}
    // Fetch new achats (purchases)
    let purchaseNotifs = [];
    try {
      const res = await purchasesAPI.getPurchases();
      purchaseNotifs = res.data.filter(p => p.status === 'nouveau' || p.status === 'new').map(p => ({
        type: 'Achat',
        id: p.id,
        reference_type: 'Achat',
        reference_id: p.id,
        date: p.created_at || p.date,
        status: 'Nouvel achat',
      }));
    } catch {}
    // Fetch new promotions
    let promotionNotifs = [];
    try {
      const res = await promotionsAPI.getPromotions();
      promotionNotifs = res.data.filter(p => p.status === 'nouveau' || p.status === 'new' || p.active).map(p => ({
        type: 'Promotion',
        id: p.id,
        reference_type: 'Promotion',
        reference_id: p.id,
        date: p.start_date || p.created_at || p.date,
        status: p.status === 'nouveau' || p.status === 'new' ? 'Nouvelle promotion' : 'Promotion active',
        name: p.name,
      }));
    } catch {}
    setNotifications([
      ...paymentNotifs,
      ...orderNotifs,
      ...invoiceNotifs,
      ...creditNoteNotifs,
      ...purchaseNotifs,
      ...promotionNotifs,
    ]);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'badge-danger';
      case 'manager':
        return 'badge-primary';
      case 'employee':
        return 'badge-success';
      default:
        return 'badge-neutral';
    }
  };

  // Icon mapping for notification types
  const notifTypeIcon = {
    Paiement: <CreditCard className="w-5 h-5 text-danger-500 mr-2" />,
    Commande: <ClipboardList className="w-5 h-5 text-primary-500 mr-2" />,
    Facture: <FileText className="w-5 h-5 text-blue-500 mr-2" />,
    Avoir: <FilePlus className="w-5 h-5 text-green-500 mr-2" />,
    Achat: <ShoppingBag className="w-5 h-5 text-yellow-500 mr-2" />,
    Promotion: <Tag className="w-5 h-5 text-pink-500 mr-2" />,
  };

  const handleMarkAsRead = (idx) => {
    setNotifications((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <header className="header">
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
        {/* Left side */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button
            onClick={onMenuToggle}
            className="btn btn-ghost btn-sm p-2"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden sm:block flex items-center">
            <img src="/logo.jpg" alt="EPOPTIQUE Logo" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" />
          </div>
        </div>
        
        {/* Center - Search */}
        <div className="flex-1 max-w-lg mx-2 sm:mx-4 hidden md:block">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              type="text"
              placeholder="Search products, sales, employees..."
              className="input pl-10 pr-3 w-full"
            />
          </div>
        </div>
        
        {/* Right side */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {isEmployee() && (
            <Link
              to="/pointage"
              className="hidden sm:inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 shadow-md shadow-rose-400/30 hover:brightness-105 transition-all"
            >
              Check-out
            </Link>
          )}

          {/* Store Info */}
          {userStore.name && (
            <div className="hidden lg:flex items-center space-x-2 text-sm text-neutral-600">
              <Store className="w-4 h-4" />
              <span className="font-medium">{userStore.name}</span>
              {userStore.city && (
                <span className="text-neutral-400">({userStore.city})</span>
              )}
            </div>
          )}
          
          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <button className="btn btn-ghost btn-sm p-2 relative" onClick={() => setDropdownOpen((open) => !open)}>
              <Bell className="w-5 h-5" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-danger-400 ring-2 ring-white animate-ping"></span>
              )}
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-80 sm:w-96 z-50">
                <ModernCard className="p-4 shadow-2xl">
                  <h4 className="text-lg font-bold mb-2">Notifications</h4>
                  {notifications.length === 0 ? (
                    <div className="text-neutral-500">Aucune notification</div>
                  ) : (
                    <ul className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
                      {notifications.map((notif, idx) => (
                        <li key={idx} className="py-2 flex items-start space-x-2">
                          {notifTypeIcon[notif.type] || <CheckCircle className="w-5 h-5 text-neutral-400 mr-2" />}
                          <ModernBadge variant={notif.type === 'Paiement' ? 'danger' : 'primary'}>{notif.type}</ModernBadge>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-neutral-800 truncate">{notif.status}</div>
                            <div className="text-xs text-neutral-500">
                              {notif.reference_type} #{notif.reference_id} {notif.amount && `- ${notif.amount} MAD`}<br/>
                              {notif.name && <span className="truncate block">Nom: {notif.name}<br/></span>}
                              {notif.date && `Date: ${notif.date}`}
                            </div>
                          </div>
                          <button
                            className="ml-2 text-xs text-primary-600 hover:underline flex-shrink-0"
                            onClick={() => handleMarkAsRead(idx)}
                          >
                            Marquer comme lu
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </ModernCard>
              </div>
            )}
          </div>
          
          {/* User menu */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="hidden sm:block text-right">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-semibold text-neutral-900">{user?.name}</p>
                <span className={`badge ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role}
                </span>
              </div>
              {userStore.name && (
                <p className="text-xs text-neutral-500 flex items-center">
                  <Store className="w-3 h-3 mr-1" />
                  {userStore.name}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
              <User className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 